import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { store } from "../../../store/configureStore";
import moment from "moment";

// components
import { FormSidebar } from "../../_commons/FormSidebar";
import { Topbar } from "../../_commons/Topbar";
import SkeletonLoader from "../SkeletonLoader";
import ShareUrlIcon from "../../_icons/ShareUrlIcon";
import GraphPeriscope from "../ActivityLog/GraphPeriscope";
import { SelectFilter } from "../../_commons/SelectFilter";

// utils
import { ActionTypes } from "../../../actions/_types";
import { capitaliseText, lS } from "../../../atlas-utils";
import {
	calculateContinuousPercentages,
	minutesToTime,
	timeToMinutes,
	transformDataa
} from "../../../helpers/periscope";
import {
	CATALOGUE_PLATFORMS_LOGO,
	periscopeTimeStatus,
	periscopeTimeStatusSelectOptions
} from "../../../client-config";

// fetch
import { fetchActivityLogLocationAvailabilityTimeSeries, periscopeFetchStoreStatus } from "../../../actions/periscope";

const StoreDetailView = ({
	isFormOpen,
	configItems,
	periscopeCountryWiseSupportedPlatforms,
	periscopeLocationAvailabilityData,
	login
}) => {
	const handleCancel = () => {
		store.dispatch({
			type: ActionTypes.LOCATION_AVAILABILITY_PERISCOPE_GRAPH_DATA,
			payload: {
				loading: false,
				error: false,
				data: {},
				sideBarOpen: false,
				storeAction: {},
				storeActionVariables: {}
			}
		});
	};
	const handleSubmit = () => {};
	const country = (login?.loggedInbizDetail?.country || lS.get("auth")?.biz?.country).toLowerCase();
	const isMultibrandEnabled = login?.loggedInbizDetail?.isMultibrandEnabled;
	const bizPlatforms = useMemo(() => {
		return configItems?.bizPlatforms?.items.filter((platform) => {
			return (periscopeCountryWiseSupportedPlatforms?.[country] ||
				lS.get("periscopeCountryWisePlatforms")?.[country])?.[platform.platformName.toLowerCase()]?.enabled;
		});
	}, [configItems?.bizPlatforms?.items, periscopeCountryWiseSupportedPlatforms?.[country]]);

	const platform = periscopeLocationAvailabilityData?.graph?.platform;
	const currPlatform =
		platform &&
		(configItems?.bizPlatforms?.items || []).length > 0 &&
		configItems?.bizPlatforms?.items.find(
			(plt) => (plt.platformName || "").toLowerCase() === (platform || "").toLowerCase()
		);
	const currRowValue = periscopeLocationAvailabilityData?.graph?.currRowValue;
	const loading = periscopeLocationAvailabilityData?.graph?.loading;
	const error = periscopeLocationAvailabilityData?.graph?.error;
	const storeAction = periscopeLocationAvailabilityData?.graph?.storeAction;
	const storeActionVariables = periscopeLocationAvailabilityData?.graph?.storeActionVariables;
	const storeActionLoading = periscopeLocationAvailabilityData?.graph?.storeActionLoading;
	const storeActionError = periscopeLocationAvailabilityData?.graph?.storeActionError;

	const storeStatus =
		currRowValue && Object.keys(currRowValue || []).length > 0
			? String(currRowValue?.[platform]?.is_available) == "true"
				? "Online"
				: "Offline"
			: "";
	const statusTextColor = storeStatus == "Offline" ? "c-b02b2b" : storeStatus == "Online" ? "c-0da125" : "";

	const data = useMemo(() => {
		return periscopeLocationAvailabilityData?.graph?.data?.data || [];
	}, [periscopeLocationAvailabilityData]);

	const fetchStoreAction = async (variables = storeActionVariables) => {
		store.dispatch({
			type: ActionTypes.LOCATION_AVAILABILITY_PERISCOPE_GRAPH_DATA,
			payload: {
				storeActionLoading: true
			}
		});
		try {
			const resp = await periscopeFetchStoreStatus(variables);

			store.dispatch({
				type: ActionTypes.LOCATION_AVAILABILITY_PERISCOPE_GRAPH_DATA,
				payload: {
					storeAction: resp,
					storeActionLoading: false
				}
			});
		} catch (err) {
			store.dispatch({
				type: ActionTypes.LOCATION_AVAILABILITY_PERISCOPE_GRAPH_DATA,
				payload: {
					storeActionLoading: false,
					storeActionError: true
				}
			});
		}
	};

	useEffect(() => {
		if (isFormOpen) {
			if (Object.keys(storeAction).length == 0) {
				fetchStoreAction();
			}
		}
	}, [isFormOpen]);

	const title =
		currRowValue &&
		`${currRowValue?.location?.name ? `${capitaliseText(currRowValue.location.name)} ` : ""}${
			isMultibrandEnabled && currRowValue?.brand?.name ? `| ${capitaliseText(currRowValue.brand.name)}` : ""
		}`;

	const tabs = useMemo(() => {
		return (
			currRowValue &&
			bizPlatforms
				.map((plt) => {
					const platformKey = (plt?.platformName || "").toLowerCase();

					if (!(platformKey in currRowValue)) {
						return null;
					}

					const value = String(currRowValue[platformKey]?.is_available);

					const cond = value === "true" || value === "false";

					if (!cond) return null;

					return {
						label: capitaliseText(plt?.platformName || ""),
						value: (plt?.platformName || "").toLowerCase(),
						id: plt?.id,
						title: capitaliseText(plt?.platformName || "")
					};
				})
				.filter(Boolean)
		);
	}, [bizPlatforms, currRowValue]);

	const switchTab = (tab, tabClick = false) => {
		const variables = periscopeLocationAvailabilityData?.graph?.variables;
		store.dispatch({
			type: ActionTypes.LOCATION_AVAILABILITY_PERISCOPE_GRAPH_DATA,
			payload: {
				platform: tab.value,
				variables: { ...variables, platform: tab.value }
			}
		});
		fetchStoreAction({
			...storeActionVariables,
			filters: [
				{ field: "platforms", value: tab.value },
				{ field: "action", value: "disable,enable" },
				{ field: "status", value: "success" },
				{ field: "created", value: "LAST_90_DAYS" }
			]
		});
		fetchActivityLogLocationAvailabilityTimeSeries({ ...variables, platform: tab.value }, "location");
	};

	const [timeOption, setTimeOption] = useState("last_7_days");
	useEffect(() => {
		setTimeOption("last_7_days");
	}, [isFormOpen]);

	const handleTimeFilter = (val) => {
		const variables = periscopeLocationAvailabilityData?.graph?.variables;
		if (val?.value) {
			setTimeOption(val.value);

			fetchActivityLogLocationAvailabilityTimeSeries({ ...variables, time: val.value }, "location");
		}
	};

	const values = (data || []).map((chunk, index) => {
		const chunkData = [...chunk.data];
		const percentages = calculateContinuousPercentages(chunkData, index, timeOption, data.length) || [];
		return percentages.reverse() || [];
	});
	let intervalScoring =
		(values || []).length > 0 &&
		(values || []).length < 2 &&
		(timeOption === "last_6_hours" ||
			timeOption === "last_2_hours" ||
			timeOption === "last_1_hour" ||
			timeOption === "last_12_hours")
			? values[0]?.[0]?.["end_time"]
			: false;

	const splitInterval = {
		last_6_hours: {
			interval: 12,
			value: 30
		},
		last_2_hours: {
			interval: 4,
			value: 30
		},
		last_1_hour: {
			interval: 4,
			value: 15
		},
		last_12_hours: {
			interval: 12,
			value: 60
		}
	};

	let timeIntervals = [];
	if (
		(data || []).length == 1 &&
		(timeOption === "last_12_hours" ||
			timeOption === "last_6_hours" ||
			timeOption === "last_2_hours" ||
			timeOption === "last_1_hour")
	) {
		let endTimeRange = timeToMinutes(intervalScoring);
		timeIntervals.push({ endTimeRange, time: intervalScoring });
		for (let i = splitInterval[timeOption]?.["interval"]; i > 0; i--) {
			endTimeRange -= splitInterval[timeOption]?.["value"];
			timeIntervals.push({ endTimeRange, time: minutesToTime(endTimeRange) });
		}
	}

	const [result, keys] = transformDataa(values);
	const processedData = result;

	const otherParamters = {};

	if (processedData.length > 15) {
		otherParamters["width"] = data.length * 48;
	}
	if (processedData.length == 15) {
		otherParamters["width"] = data.length * 42;
	}
	const numbers =
		(data || []).length == 1 &&
		(timeOption == "last_1_hour" || timeOption == "last_2_hours" || timeOption == "last_12_hours")
			? Array.from({ length: 5 }, () => data.length)
			: Array.from({ length: 25 }, () => data.length);
	function getDayOfWeeks(dateString) {
		const date = new Date(dateString);
		const options = { weekday: "long" };
		return date.toLocaleDateString("en-US", options);
	}

	const formatTime = (hour) => {
		let period = hour >= 12 ? "PM" : "AM";
		let displayHour = hour % 12;
		displayHour = displayHour === 0 ? 12 : displayHour;
		return `${displayHour}:00 ${period}`;
	};

	let reason = "";
	if (storeAction?.reasonCode) {
		const formatted = storeAction.reasonCode.split("_").map(capitaliseText).join(" ");
		reason = formatted.length > 35 ? formatted.slice(0, 35) + "..." : formatted;
	}
	let updatedStoreTime = "";
	if (storeAction?.updated) {
		const date = moment(storeAction?.updated);
		const now = moment();

		if (date.year() === now.year()) {
			updatedStoreTime = date.format("DD MMM, hh:mm A");
		} else {
			updatedStoreTime = date.format("DD MMM YYYY, hh:mm A");
		}
	}

	const user =
		(storeAction && storeAction?.user?.firstName) ||
		storeAction?.user?.lastName ||
		storeAction?.user?.fullName ||
		storeAction?.user?.email;

	return (
		<div className="sidebar-graph-periscope">
			<FormSidebar
				isOpen={isFormOpen}
				close={handleCancel}
				submit={handleSubmit}
				loading={false}
				disabled={true}
				hideSubmitAction={true}
				cancelTitle={"Close"}
				cancelClass={"close-btn"}
			>
				<Header title={title} handleCancel={handleCancel} />
				{tabs && tabs.length > 0 && (
					<Topbar tabs={tabs} selectedTab={platform} switchTab={(tab) => switchTab(tab, true)} />
				)}
				<div className="side-bar-location-content">
					<div className="store-action">
						<div className="action-details">
							{currPlatform && (
								<img
									className="plt-img"
									src={
										currPlatform?.image ||
										CATALOGUE_PLATFORMS_LOGO[currPlatform.platformName.toLowerCase()]
									}
								/>
							)}

							<div className="action-info">
								{storeActionLoading || storeActionError ? (
									<div className="action-loading">
										<SkeletonLoader classname={"large"} />
										<SkeletonLoader classname={"small"} />
									</div>
								) : (
									<>
										{updatedStoreTime && (
											<div className="final-text">
												<span className="normal-txt">Set</span>
												<span className={`store-status ${statusTextColor} `}>
													{storeStatus}
												</span>
												{user && <span className="normal-txt">by</span>}
												<span className="bold-text">{user}</span>
												{updatedStoreTime && <span className="normal-txt">at</span>}
												<span className="bold-text">{updatedStoreTime}</span>
											</div>
										)}

										{!storeActionLoading && (
											<div className="reason-txt">
												{reason ? (
													<span>{reason}</span>
												) : !(
														storeAction &&
														Object.prototype.hasOwnProperty.call(storeAction, "updated")
												  ) ? (
													<div>No actions in the last 90 days</div>
												) : (
													""
												)}
											</div>
										)}
									</>
								)}
							</div>
						</div>
						<div>
							{platform && (
								<div
									className="plt-link"
									onClick={() => {
										window.open(currRowValue?.[(platform || "").toLowerCase()]?.url, "_blank");
									}}
								>
									View on {capitaliseText(platform)}{" "}
									<ShareUrlIcon styles={{ marginLeft: "3px" }} color="#3B5FEB" />
								</div>
							)}
						</div>
					</div>

					<div className="action-stats">
						<div className="mini-head">Availability Stats</div>
						<div>
							<SelectFilter
								containerClasses="location-time-dropdown"
								options={periscopeTimeStatusSelectOptions}
								field="timeOption"
								currValue={{
									value: timeOption,
									valueForDisplay: (
										<span className="status-main">
											<span className="status-display">{periscopeTimeStatus[timeOption]}</span>
										</span>
									)
								}}
								labelKey="valueForDisplay"
								valueKey="value"
								setFilter={(f, v) => handleTimeFilter(v)}
								isSearchable={false}
								isClearable={false}
							/>
						</div>
					</div>

					<div className={`graph-div   ${loading ? `loading-div` : ``}  ${error ? " error" : "flex"} `}>
						{loading || error ? (
							<>
								<div className="shimmer H(100px)  " />
								<div className="shimmer H(100px) Mt(5px) " />
								<div className="shimmer H(100px) Mt(5px) " />
							</>
						) : (
							(data || []).length !== 0 && (
								<>
									<div
										className={`time-series flex ${
											timeOption == "last_1_hour" && data.length == 1 ? "mt-70" : ""
										}`}
									>
										<div
											className={`time-text ${
												!intervalScoring ? "justify-center" : "justify-between"
											}`}
										>
											{!intervalScoring
												? numbers.map((num, index) => {
														return (
															<div className="time">
																{index == 0 ||
																index == 6 ||
																index == 12 ||
																index == 18 ||
																index == 24
																	? index == 24
																		? "11:59PM"
																		: formatTime(index)
																	: ""}
															</div>
														);
													})
												: timeIntervals.reverse().map((time) => {
														return (
															<div className={`${time?.time ? "time" : ""}`}>
																{time?.time}
															</div>
														);
													})}
										</div>
										<div
											className={`border-interval ${
												timeOption == "last_1_hour" ||
												timeOption == "last_2_hours" ||
												timeOption == "last_6_hours"
													? "justify-between"
													: ""
											}`}
										>
											{numbers.map((num, index) => {
												return (
													<div
														className={`indicator ${
															timeOption == "last_1_hour" && data.length == 1
																? "justify-between"
																: "h-13"
														} `}
													>
														<div className="div-with"></div>
													</div>
												);
											})}
										</div>
									</div>
									<div
										className={`graph-content   ${processedData.length > 15 ? "scroll-x-hidden" : ""}`}
									>
										<div
											className={`date-display 
																	${processedData.length <= 14 ? "len-four-teen" : ""}
																	${processedData.length === 15 ? "len-five-teen" : ""}
																	${processedData.length >= 30 ? "len-three-ty" : ""}
																  `}
										>
											{processedData.map((num, index) => {
												return (
													<div
														className={`date-val ${
															processedData.length > 15 ? "len-greater-five-teen" : ""
														}
																		${processedData.length > 15 ? "len-greater-five-teen" : "less-five-teen"}
																		${processedData.length == 15 ? "len-eq-five-teen" : ""}
																		${index == 0 && processedData.length !== 15 ? "index-zero" : ""}
																		${processedData.length == 31 ? "len-three-one" : ""}
																		${index == 0 && processedData.length == 31 ? "index-zero-three-one" : ""}`}
														style={{
															...(processedData.length > 15
																? {}
																: {
																		width: "100%",
																		flexBasis: `${100 / (processedData || []).length}%`
																	})
														}}
													>
														<div className="date-txt">
															<div className="week">
																{processedData.length > 7
																	? getDayOfWeeks(
																			processedData[index]?.["day"]
																		).slice(0, 3)
																	: getDayOfWeeks(processedData[index]?.["day"])}
															</div>
															<div className="date">
																{processedData[index]?.day?.split("-")[2]}
															</div>
														</div>
													</div>
												);
											})}
										</div>
										<GraphPeriscope
											otherParamters={otherParamters}
											modified={processedData}
											keys={keys}
										/>
									</div>
								</>
							)
						)}
					</div>
				</div>
			</FormSidebar>
		</div>
	);
};

const mapStateToProps = (state) => ({
	configItems: state?.configItems,
	periscopeCountryWiseSupportedPlatforms: state.periscopeCountryWiseSupportedPlatforms,
	periscopeLocationAvailabilityData: state.periscopeLocationAvailabilityData,
	login: state.login
});
export default connect(mapStateToProps)(StoreDetailView);

const Header = ({ handleCancel, title }) => {
	return (
		<div className="header-sidebar" style={{ padding: "24px" }}>
			<div className="mini-div" onClick={() => handleCancel()}>
				<span className="mini-title">Storefront Availability</span>
				<img className="link-img" src="/assets/periscope/right-arrow-action.svg" />
			</div>
			<div className="main-title" style={{ marginTop: "8px" }}>
				{title}
			</div>
		</div>
	);
};
